import { useState, type CSSProperties } from 'react';
import type { MenuItem } from '@/types';
import { useTranslation } from 'react-i18next';
import { useCatalogText } from '@/lib/localizeCatalog';
import Price, { formatPrice } from '@/components/ui/Price';
import SteamLayer from './SteamLayer';
import { thumbPhoto } from './photoVariant';
import ProductImage from './ProductImage';
import { useRevealOnScroll } from './useRevealOnScroll';
import { useStyleMode } from '@/features/restaurant/useStyleMode';
import { useAppSelector } from '@/app/hooks';
import { selectIsInDineIn, selectIsBuffetDineIn } from '@/features/tableSession/tableSessionSlice';
import { selectCartMode } from '@/features/cart/cartSlice';
import { resolveModePrice } from './resolveModePrice';
import { fromPriceCents } from './fromPrice';
import DishCodeTag from './DishCodeTag';
import AllergenRow from './allergens/AllergenRow';
import type { AllergenCode } from './allergens/allergens';

interface Props {
  item: MenuItem;
  onClick: (sourceEl: HTMLElement) => void;
  onQuickAdd?: (item: MenuItem, sourceEl: HTMLElement) => void;
  orderingEnabled?: boolean;
  restaurantLogoUrl: string | null;
  restaurantName: string;
  themeColor: string;
  restaurantUuid: string;
  /** Position within its category — drives the entrance stagger. */
  index?: number;
  /** SC-1280 — allergènes déclarés du plat ; `undefined` ou vide ⇒ rien d'affiché. */
  allergens?: AllergenCode[];
}

const BADGE_STYLE: Record<NonNullable<MenuItem['badges']>[number], string> = {
  popular: 'bg-mint-deep text-paper',
  new: 'bg-mint text-[var(--on-primary)]',
  spicy: 'bg-hot-pink text-[var(--on-accent)]',
};

export default function DishCard({
  item,
  onClick,
  onQuickAdd,
  orderingEnabled = false,
  restaurantLogoUrl,
  restaurantName,
  themeColor,
  restaurantUuid,
  index = 0,
  allergens
}: Props) {
  const { t } = useTranslation();
  const ct = useCatalogText();
  const itemName = ct.name(item);
  const itemDescription = ct.description(item);
  const styleMode = useStyleMode();
  // DF-0161 — un groupe obligatoire du plat a ete desactive : plus rien n'est
  // cochable et `resource_api` refuse le devis. Sans ce drapeau la fiche
  // perdait tous ses groupes et le « + » vendait le plat a plein tarif, sans
  // choix, avec un bon de cuisine ne portant que son nom.
  const unavailable = !!item.unavailableReason;
  const showQuickAdd = orderingEnabled && !!onQuickAdd && !unavailable;
  const isInDineIn = useAppSelector(selectIsInDineIn);
  const isBuffetDineIn = useAppSelector(selectIsBuffetDineIn);
  const cartMode = useAppSelector(selectCartMode(restaurantUuid));
  const activeMode = isInDineIn ? 'dine_in' : cartMode;
  const displayPrice = resolveModePrice(item, activeMode);
  // SC-218: a buffet-included product shows "Inclus" instead of its price in a
  // buffet dine-in session (the buffet entry fee covers it). À-la-carte unchanged.
  const showInclus = isBuffetDineIn && !!item.includedInBuffet;
  // Size/quantity variants carry their price in a required option group, so the
  // raw price is 0 — the card announced a free dish. Show what the cheapest
  // choice costs instead; the modal still resolves the exact price.
  const fromCents = displayPrice === 0 && !showInclus ? fromPriceCents(item) : null;

  // Entrance: fade + slide in once, when the card scrolls into view.
  const { ref: cardRef, revealed } = useRevealOnScroll<HTMLElement>();
  // After the entrance animation ends we drop to a plain .is-revealed class so
  // the article's `animation` slot is free for the hover wobble — otherwise
  // unhovering would re-apply (and replay) the entrance animation.
  const [revealDone, setRevealDone] = useState(false);
  const revealClass = revealDone ? 'is-revealed' : revealed ? 'is-revealing' : '';
  // Press: one-shot squish on the image frame, triggered on pointer-down.
  const [pressed, setPressed] = useState(false);

  return (
    <article
      ref={cardRef}
      data-menu-item
      className={`dish-card-root dish-card--wobble group flex flex-col bg-paper border-2 border-ink w-full transition-all duration-300 ease-out hover:-translate-y-[2px] ${revealClass}`}
      style={{ boxShadow: 'var(--shadow-stamp)', '--reveal-delay': `${(index % 4) * 110}ms` } as CSSProperties}
      onAnimationEnd={(e) => { if (e.animationName === 'dishReveal') setRevealDone(true); }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-stamp-lg)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-stamp)'; }}
    >
      <div className="relative">
        <button
          type="button"
          onClick={(e) => onClick(e.currentTarget)}
          onPointerDown={() => setPressed(true)}
          aria-label={itemName}
          className="block w-full p-0 m-0 bg-transparent border-0 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint"
        >
          <div
            className={`dish-frame aspect-[4/3] bg-ash overflow-hidden border-b-2 border-ink relative ${pressed ? 'dish-press' : ''}`}
            onAnimationEnd={(e) => { if (e.animationName === 'dishPress') setPressed(false); }}
          >
            <ProductImage
              photo={thumbPhoto(item.photo)}
              restaurantLogoUrl={restaurantLogoUrl}
              restaurantName={restaurantName}
              themeColor={themeColor}
              alt={itemName}
              variant="card"
              className="transition-transform duration-300 ease-out group-hover:scale-[1.04]"
            />
            {item.hot && !unavailable && <SteamLayer />}
            {unavailable && (
              <div className="absolute inset-0 flex items-center justify-center bg-paper/70 px-2">
                <span
                  data-testid={`dish-unavailable-${item.id}`}
                  className="border-2 border-ink bg-paper px-2 py-1 text-center font-display text-[11px] leading-[1.15] text-ink sm:text-[12px]"
                  style={{ boxShadow: 'var(--shadow-stamp-sm)' }}
                >
                  {t('menu.unavailable')}
                </span>
              </div>
            )}
            {item.badges?.length ? (
              <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-wrap gap-1.5 sm:gap-2 max-w-[calc(100%-1rem)]">
                {item.badges.map((b) => {
                  const badgeLabel = t(`menu.badges.${b}`);
                  const label = styleMode === 'premium' ? badgeLabel.replace(/^\S+\s+/, '') : badgeLabel;
                  return (
                    <span
                      key={b}
                      className={`px-1.5 py-0.5 sm:px-2 sm:py-1 text-[9px] sm:text-[10px] font-display border-2 border-ink leading-none ${BADGE_STYLE[b]}`}
                      style={{ boxShadow: 'var(--shadow-stamp-sm)' }}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>
        </button>
        {showQuickAdd && (
          <button
            type="button"
            aria-label={t('menu.quickAdd', { name: itemName })}
            onClick={(e) => {
              e.stopPropagation();
              onQuickAdd!(item, e.currentTarget);
            }}
            className={`absolute bottom-2 end-2 sm:bottom-3 sm:end-3 w-10 h-10 flex items-center justify-center font-display text-[22px] leading-none cursor-pointer transition-all hover:-translate-y-[1px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint ${
              styleMode === 'premium'
                ? 'rounded-full bg-mint text-[var(--on-primary)] border border-[var(--border-color)]'
                : 'bg-hot-pink text-[var(--on-accent)] border-2 border-ink hover:-translate-x-[1px]'
            }`}
            style={{ boxShadow: 'var(--shadow-stamp)' }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-stamp-lg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-stamp)'; }}
          >
            +
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => onClick(e.currentTarget)}
        className="flex grow flex-col w-full text-left p-2 sm:p-3 m-0 bg-transparent border-0 text-ink cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint"
      >
        {/* grow + mt-auto sur la ligne de prix : les cartes d'une même rangée
            ont la même hauteur (grid stretch), donc les prix s'alignent même
            quand une carte a une description et sa voisine non. */}
        <div className="flex grow flex-col gap-0.5 sm:gap-1">
          <h3 className="font-display text-ink text-[15px] sm:text-[18px] leading-[1.15] m-0 line-clamp-2"><DishCodeTag name={itemName} code={item.code} />{itemName}</h3>
          {/* Rendered only when non-empty — an empty <p> still spent a flex gap
              above and below it, which read as dead space on description-less menus. */}
          {itemDescription ? (
            <p className="font-mono text-[12px] sm:text-[13px] text-[var(--ink-faint)] leading-[1.4] line-clamp-2 m-0 whitespace-pre-line">
              {itemDescription}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-2 mt-auto pt-1.5 sm:pt-2 leading-none border-t-[1px] border-ink/15">
            {/* SC-1280 : les pictogrammes d'allergènes suivent le prix sur la même
                ligne (mesuré à 360 px : « 12,90 € » + 4 pictos + « +2 » tient, carte
                de même hauteur). Si le prix est long (« à partir de … », prix barré)
                la rangée passe dessous en entier plutôt que d'être rognée ou de
                faire revenir le prix à la ligne : un allergène ne se tait pas. */}
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              {showInclus ? (
                <span className="font-display text-[14px] sm:text-[15px] text-mint-deep">{t('menu.includedInBuffet')}</span>
              ) : fromCents !== null ? (
                <span className="font-mono font-bold text-ink text-[14px] sm:text-[15px]">
                  {t('menu.priceFrom', { price: formatPrice(fromCents) })}
                </span>
              ) : (
                <Price amountCents={displayPrice} saleCents={item.salePrice} className="text-[14px] sm:text-[15px]" />
              )}
              <AllergenRow codes={allergens} />
            </div>
            <span className="hidden sm:inline shrink-0 whitespace-nowrap font-mono text-[11px] underline text-[var(--ink-mute)] group-hover:text-mint-deep transition-colors">
              {unavailable ? t('menu.unavailable') : t('menu.quickView')}
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}
